import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DeleteButton } from '@/components/DeleteConfirmDialog';
import { Package, Plus, Edit, Circle } from 'lucide-react';

// Kapseltyp-Optionen
const CAPSULE_TYPES = [
  { value: 'NONE', label: 'KEINE' },
  { value: 'K10', label: 'K-10' },
  { value: 'K11', label: 'K-11' },
  { value: 'K11H', label: 'K-11H' },
  { value: 'K_GOLIATH', label: 'K-Goliath' },
];

// Hilfsfunktion zur Anzeige des Kapseltyps
const getCapsuleTypeLabel = (value) => {
  const type = CAPSULE_TYPES.find(t => t.value === value);
  return type ? type.label : 'KEINE';
};

const ProdukteVerwaltungPage = () => {
  const { API, user } = useContext(AuthContext);
  const [produkte, setProdukte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    purchase_price: 0,
    sale_price: 0,
    default_fill_quantity: 50,
    min_stock: 100,
    category: '',
    active: true,
    capsule_type: 'NONE',
  });

  useEffect(() => {
    fetchProdukte();
  }, []);

  const fetchProdukte = async () => {
    try {
      const response = await axios.get(`${API}/produkte`);
      setProdukte(response.data || []);
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Produkte');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Produkte');
      }
      // Bei 404 oder anderen Fehlern: leere Liste anzeigen ohne Toast
      setProdukte([]);
      console.error('Produkte laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/produkte`, formData);
      toast.success('Produkt erstellt!');
      setShowCreateDialog(false);
      fetchProdukte();
      setFormData({
        name: '',
        description: '',
        purchase_price: 0,
        sale_price: 0,
        default_fill_quantity: 50,
        min_stock: 100,
        category: '',
        active: true,
        capsule_type: 'NONE',
      });
    } catch (error) {
      toast.error('Fehler beim Erstellen');
      console.error(error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      purchase_price: product.purchase_price,
      sale_price: product.sale_price,
      default_fill_quantity: product.default_fill_quantity,
      min_stock: product.min_stock,
      category: product.category || '',
      active: product.active !== false,
      capsule_type: product.capsule_type || 'NONE',
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/produkte/${editingProduct.id}`, formData);
      toast.success('Produkt aktualisiert!');
      setShowEditDialog(false);
      setEditingProduct(null);
      fetchProdukte();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
      console.error(error);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Produkte
          </h1>
          <p className="text-slate-600">Verwalte deine Produktpalette</p>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600">
                <Plus size={20} />
                <span>Neu</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Neues Produkt erstellen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pb-4">
                <div>
                  <Label>Produktname</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="z.B. Erdnuss Kaugummi"
                  />
                </div>
                <div>
                  <Label>Beschreibung</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    placeholder="Produktbeschreibung"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Einkaufspreis (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Verkaufspreis (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.sale_price}
                      onChange={(e) => setFormData({ ...formData, sale_price: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Standard-Füllmenge</Label>
                    <Input
                      type="number"
                      value={formData.default_fill_quantity}
                      onChange={(e) => setFormData({ ...formData, default_fill_quantity: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Mindestbestand</Label>
                    <Input
                      type="number"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Bezugsquelle</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="z.B. Großhändler XY"
                  />
                </div>
                <div>
                  <Label>Kapsel *</Label>
                  <Select
                    value={formData.capsule_type}
                    onValueChange={(value) => setFormData({ ...formData, capsule_type: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kapseltyp auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAPSULE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                  <Label>Aktiv</Label>
                </div>
                <Button type="submit" className="w-full">
                  Erstellen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {produkte.length === 0 ? (
        <Card className="p-12">
          <div className="text-center text-slate-500">
            <Package size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg">Noch keine Produkte vorhanden</p>
            {user?.role === 'admin' && (
              <p className="text-sm mt-2">Erstelle dein erstes Produkt</p>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {produkte.map((produkt) => (
            <Card key={produkt.id} className="p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-3">
                  <Package className="text-green-600" size={24} />
                  <div>
                    <h3 className="font-bold text-lg">{produkt.name}</h3>
                    {produkt.category && (
                      <p className="text-sm text-slate-500">{produkt.category}</p>
                    )}
                  </div>
                </div>
                {user?.role === 'admin' && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(produkt)}
                      className="hover:bg-slate-100"
                    >
                      <Edit size={16} />
                    </Button>
                    <DeleteButton
                      onDelete={async () => {
                        try {
                          await axios.delete(`${API}/produkte/${produkt.id}`);
                          toast.success('Produkt gelöscht!');
                          fetchProdukte();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
                          throw error;
                        }
                      }}
                      title="Produkt löschen"
                      itemName={produkt.name}
                    />
                  </div>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                {produkt.description && (
                  <p className="text-slate-600">{produkt.description}</p>
                )}
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-slate-500">Einkauf:</span>
                  <span className="font-medium">{produkt.purchase_price.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Verkauf:</span>
                  <span className="font-medium text-green-600">{produkt.sale_price.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Füllmenge:</span>
                  <span className="font-medium">{produkt.default_fill_quantity} Stück</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Kapsel:</span>
                  <span className={`font-medium ${produkt.capsule_type && produkt.capsule_type !== 'NONE' ? 'text-blue-600' : 'text-slate-400'}`}>
                    {getCapsuleTypeLabel(produkt.capsule_type)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status:</span>
                  <span className={`font-medium ${produkt.active !== false ? 'text-green-600' : 'text-red-600'}`}>
                    {produkt.active !== false ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Produkt bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Produktname</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Einkaufspreis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div>
                <Label>Verkaufspreis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Standard-Füllmenge</Label>
                <Input
                  type="number"
                  value={formData.default_fill_quantity}
                  onChange={(e) => setFormData({ ...formData, default_fill_quantity: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div>
                <Label>Mindestbestand</Label>
                <Input
                  type="number"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Bezugsquelle</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>
            <div>
              <Label>Kapsel *</Label>
              <Select
                value={formData.capsule_type}
                onValueChange={(value) => setFormData({ ...formData, capsule_type: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kapseltyp auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {CAPSULE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <Label>Aktiv</Label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                Aktualisieren
              </Button>
              <DeleteButton
                onDelete={async () => {
                  try {
                    await axios.delete(`${API}/produkte/${editingProduct.id}`);
                    toast.success('Produkt gelöscht!');
                    setShowEditDialog(false);
                    fetchProdukte();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
                    throw error;
                  }
                }}
                title="Produkt löschen"
                itemName={editingProduct?.name}
                size="default"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
              />
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProdukteVerwaltungPage;